// Polyfill Buffer for aws-appsync-subscription-link

import { Buffer } from "buffer";
import { Global } from "@emotion/react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { ErrorBoundary as SentryErrorBoundary } from "@sentry/react";
import { AnalyticsContextProvider } from "contexts/AnalyticsContext";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { env } from "services/configuration/env";
import { App } from "./App";
import { ConnectionErrorModal } from "./components/global/modals/ConnectionErrorModal";
import { initSentry } from "./services/external/sentry";
import { store } from "./store";
import { installNetworkDebug } from "./utils/debug/installNetworkDebug";
import { installSubscriptionLivenessTap } from "./utils/installSubscriptionLivenessTap";
import EAT_PERFECT_THEME from "./utils/theme";

window.Buffer = Buffer;

// TEMP network diagnostic: install the fetch wrapper + connectivity listeners before any request runs.
// Self-gated by the ?networkDebug flag, so it's a no-op on normal customer tablets.
installNetworkDebug();
// Observe inbound AppSync `ka`/frames for the liveness watchdog. Must run before the Apollo client
// opens its WebSocket, so install it here at bootstrap.
installSubscriptionLivenessTap();

const enableMocking = async () => {
	if (!env.VITE_MSW_ENABLED) {
		return;
	}

	const { worker } = await import("./tests/mocks/browser");
	await worker.start();
	window.MSW = {};
};

initSentry();

enableMocking().then(() => {
	createRoot(document.getElementById("root") as HTMLElement).render(
		<StrictMode>
			<ThemeProvider theme={EAT_PERFECT_THEME}>
				<CssBaseline />
				<Global
					styles={{
						".MuiDrawer-root > .MuiPaper-root": {
							height: `calc(50% - ${320}px)`,
							overflow: "visible",
						},
						body: {
							overflow: "unset",
						},
					}}
				/>
				<Provider store={store}>
					<AnalyticsContextProvider>
						<SentryErrorBoundary fallback={<ConnectionErrorModal appConnected={false} />}>
							<App />
						</SentryErrorBoundary>
					</AnalyticsContextProvider>
				</Provider>
			</ThemeProvider>
		</StrictMode>,
	);
});
